'use client';

import { c as _c } from "react/compiler-runtime";
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { type FC, type PropsWithChildren, type ReactNode } from 'react';
import { SIDEBAR_WIDTH } from '@/app/theme';
import { type Theme, type CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Image from 'next/image';
import { TOP_BAR_HEIGHT } from '@/app/theme';
import resoneticsLogoUrl from '@/public/resonetics.svg';
const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  }
});
export const DrawerHeader: FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Box sx={_temp} component="div">{children}</Box>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface SideMenuProps {
  isMenuOpen: boolean;
  children: ReactNode;
  onToggleMenu: () => void;
}
export const SideMenu: FC<SideMenuProps> = t0 => {
  const $ = _c(15);
  const {
    isMenuOpen,
    onToggleMenu,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      sx: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isMenuOpen) {
    t2 = theme_0 => ({
      width: SIDEBAR_WIDTH,
      flexShrink: 0,
      whiteSpace: "nowrap",
      boxSizing: "border-box",
      ...(isMenuOpen && {
        ...openedMixin(theme_0),
        "& .MuiDrawer-paper": openedMixin(theme_0)
      }),
      ...(!isMenuOpen && {
        ...closedMixin(theme_0),
        "& .MuiDrawer-paper": closedMixin(theme_0)
      })
    });
    $[1] = isMenuOpen;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Image src={resoneticsLogoUrl} priority={true} height={TOP_BAR_HEIGHT - 25} width={170} alt="Resonetics" style={{
      aspectRatio: 1
    }} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronLeft />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onToggleMenu) {
    t5 = <DrawerHeader>{t3}<IconButton onClick={onToggleMenu} color="primary">{t4}</IconButton></DrawerHeader>;
    $[5] = onToggleMenu;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      overflowY: "auto"
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      py: 0,
      height: "100%",
      position: "relative",
      width: "100%",
      overflowX: "hidden",
      overflowY: "hidden",
      "&:hover": t6,
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(255, 255, 255, 0.35) transparent",
      "&::-webkit-scrollbar": {
        width: 8
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent"
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(255, 255, 255, 0.35)",
        borderRadius: 999,
        border: "1px solid rgba(255, 255, 255, 0.25)"
      },
      "&:hover::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(255, 255, 255, 0.55)",
        borderColor: "rgba(255, 255, 255, 0.35)"
      }
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== children) {
    t8 = <List sx={t7}>{children}</List>;
    $[9] = children;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t2 || $[12] !== t5 || $[13] !== t8) {
    t9 = <Drawer variant="permanent" PaperProps={t1} sx={t2}>{t5}{t8}</Drawer>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
function _temp(theme) {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    backgroundColor: theme.palette.common.white,
    ...theme.mixins.toolbar
  };
}
function _temp2(theme) {
  return {
    backgroundColor: theme.palette.primary.main
  };
}